/* global dataLayer*/

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  getPagePath,
  useSoyuzLocales,
  useSoyuzFlag
} from '@spotahome/soyuz/client';
import { useEcommerceManagerFactory } from '@spotahome/ecommerce-tracking';

import Tracking from '@spotahome/soyuz-tracking';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import internalLinkingModule from '@spotahome/marketplace-common/src/shapes/internalLinking';

import { configureMetricsActions } from '@spotahome/marketplace-common/src/graphql/mutations/tenantActions';

import { PricingProvider } from '@spotahome/marketplace-common/src/context/PricingContext';
import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';

import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';

import {
  CHANNELS,
  formatDate,
  useMediatorModalHandle
} from '@spotahome/ui-library';
import ListingDatesValidatorService from '@spotahome/ui-library/src/tenant/components/utils/listing-dates-validator-service';

import VerificationModal from '@spotahome/ui-library/src/tenant/components/ListingLabel/VerificationModal/VerificationModal';
import CheckedIconPhoto from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/ChekedIconPhoto';

import { Mediator } from '@spotahome/soyuz-mediator';

import { BOOKING_STATUSES } from '../../constants';

import Listing from '../Listing';
import NotFound from '../NotFound';
import { ListingNavigationProvider } from '../../context/ListingNavigationContext';
import { getBookingStatus } from '../../utils/getBookingStatus';

const getInitialState = props => {
  const { listing, moveIn, moveOut } = props;
  let selectedMoveIn;
  let selectedMoveOut;
  if (moveIn) {
    const isInRange = ListingDatesValidatorService.isMoveInDateValid(
      moveIn,
      listing.moveInDatesRanges
    );

    if (!isInRange) {
      selectedMoveIn = formatDate(
        ListingDatesValidatorService.getMinAvailableDate(
          listing.moveInDatesRanges
        )
      );
    } else {
      selectedMoveIn = moveIn;
      selectedMoveOut = moveOut;
    }
  }

  return {
    moveIn: selectedMoveIn,
    moveOut: selectedMoveOut,
    bookingStatus: BOOKING_STATUSES.EMPTY_VALIDATION
  };
};

const HOTJAR_POTENTIAL_PERMANENT_BOOKING = 'potential_permanent_booking';

const trackHotjarPermanentContract = currentLocale => {
  Tracking.hotjar.recordTag(HOTJAR_POTENTIAL_PERMANENT_BOOKING);
  Tracking.hotjar.triggerEvent(
    `${HOTJAR_POTENTIAL_PERMANENT_BOOKING}_${currentLocale}`
  );
  Tracking.hotjar.triggerEvent('permanent_booking_listing');
};

const ShowRentableUnit = ({
  listing = null,
  areaSlug,
  moveIn = '',
  moveOut = '',
  isLogged = false,
  userId,
  isRoomsAggregator = false,
  listingCanonicalUrl,
  hideHeader = false,
  deviceType,
  deviceOS = '',
  internalLinking,
  landlordHomecards = []
}) => {
  const locales = useSoyuzLocales();
  const ecommerceManager = useEcommerceManagerFactory({
    sendInHouseAnalytics: useSoyuzFlag('SendInhouseSalesEvents').isOn()
  });
  const [state, setState] = useState(
    getInitialState({ listing, moveIn, moveOut })
  );

  const openVerificationModal = () => {
    SoyuzAnalytics.sendGA4Event('open', {
      section: 'more-about-verification'
    });
  };

  const { isModalOpen, handleModalClose } = useMediatorModalHandle({
    channel: CHANNELS.OPEN_VERIFICATION_MODAL,
    onOpen: openVerificationModal
  });

  useEffect(() => {
    if (!listing) {
      return;
    }

    configureMetricsActions();
    Tracking.actions.sendActionEvent(Tracking.actions.VIEW_PROPERTY, {
      listingId: listing.id
    });

    ecommerceManager.trackProductDetail({
      listing,
      isLogged,
      locale: locales.current,
      moveIn,
      moveOut
    });

    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({ userId });
    }

    if (listing.allowsPermanentContract) {
      trackHotjarPermanentContract(locales.current);
    }

    if (typeof dataLayer !== 'undefined' && Array.isArray(dataLayer)) {
      dataLayer.push({
        remarketing_listing_id: listing.id,
        remarketing_listing_pagetype: 'Productpage',
        remarketing_listing_total_value: listing.pricing.price
      });
    }
  }, []);

  const handleBookingDatesReset = () => {
    setState({
      bookingStatus: BOOKING_STATUSES.EMPTY_VALIDATION,
      moveIn: '',
      moveOut: ''
    });

    AdUrlService.deleteQueryParameter('move-in');
    AdUrlService.deleteQueryParameter('move-out');
  };

  useEffect(() => {
    Mediator.subscribe(
      CHANNELS.REFRESH_CALENDAR_DATEPICKER,
      handleBookingDatesReset
    );

    return () => {
      Mediator.unsubscribe(CHANNELS.REFRESH_CALENDAR_DATEPICKER);
    };
  }, []);

  const handleBookingDatesChange = async ({
    moveIn: newMoveIn,
    moveOut: newMoveOut
  }) => {
    const bookingStatus = getBookingStatus(newMoveIn, newMoveOut, listing);

    setState(prevState => {
      const newState = {};

      if (newMoveIn) {
        newState.moveIn = newMoveIn;
      }

      newState.moveOut = newMoveOut;

      return {
        ...prevState,
        ...newState,
        bookingStatus
      };
    });

    AdUrlService.setLocationUrl({
      ...(newMoveOut ? { 'move-in': newMoveIn } : null),
      ...(newMoveOut ? { 'move-out': newMoveOut } : null)
    });

    if (newMoveIn) {
      Tracking.actions.sendActionEvent(Tracking.actions.SELECT_DATES, {
        listingId: listing.id,
        moveIn: newMoveIn,
        moveOut: `${newMoveOut}`
      });
    }
  };

  const hasGAP = () =>
    parseInt(listing.bookingAcceptanceMaxDays, 10) >= 0 ||
    parseInt(listing.bookingAcceptanceMinDays, 10) > 0;

  const isWholeApartment = () =>
    listing.type === 'whole' || listing.type === 'studio';

  const mapSearchHref = getPagePath('marketplace.free-search', {
    params: {
      locationString: areaSlug
    },
    query: {
      'move-in': state.moveIn,
      'move-out': state.moveOut
    }
  });

  if (!listing) {
    return <NotFound exploreButtonHref={mapSearchHref} />;
  }

  const {
    allowsTemporalContract,
    allowsPermanentContract,
    pricing,
    bookingConditions,
    id,
    plan,
    cityId
  } = listing;

  return (
    <section className="l-main-section">
      <PricingProvider
        allowsTemporalContract={allowsTemporalContract}
        allowsPermanentContract={allowsPermanentContract}
        listingPricing={pricing}
        bookingConditions={bookingConditions}
        listingId={id}
        listingPlan={plan}
        checkInDate={state.moveIn}
        checkOutDate={state.moveOut}
        cityId={cityId}
      >
        <ListingNavigationProvider>
          <Listing
            hasGAP={hasGAP()}
            isRoomsAggregator={isRoomsAggregator}
            isWholeApartment={isWholeApartment()}
            listing={listing}
            listingCanonicalUrl={listingCanonicalUrl}
            locale={locales.current}
            moveIn={state.moveIn}
            moveOut={state.moveOut}
            onBookingDatesChange={handleBookingDatesChange}
            hideHeader={hideHeader}
            bookingStatus={state.bookingStatus}
            deviceType={deviceType}
            deviceOS={deviceOS}
            internalLinking={internalLinking}
            landlordHomecards={landlordHomecards}
          />
        </ListingNavigationProvider>
      </PricingProvider>
      <VerificationModal
        isOpen={isModalOpen}
        handleClose={handleModalClose}
        checkedIcon={
          listing.checked ? (
            <CheckedIconPhoto photoUrl={listing.runner?.photoUrl} isVerified />
          ) : null
        }
      />
    </section>
  );
};

ShowRentableUnit.propTypes = {
  areaSlug: PropTypes.string.isRequired,
  isRoomsAggregator: PropTypes.bool,
  listing: PropTypes.shape(ListingShape),
  listingCanonicalUrl: PropTypes.string.isRequired,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  isLogged: PropTypes.bool,
  hideHeader: PropTypes.bool,
  userId: PropTypes.string.isRequired,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired,
  deviceOS: PropTypes.string,
  internalLinking: PropTypes.arrayOf(PropTypes.shape(internalLinkingModule))
    .isRequired,
  landlordHomecards: PropTypes.arrayOf(PropTypes.object)
};

export default ShowRentableUnit;
